import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  forwardRef,
  inject,
  NgZone,
  OnInit,
  output,
  signal,
  viewChild
} from '@angular/core';
import {
  ReactiveFormsModule,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';

declare const google: any;

@Component({
  selector: 'app-google-autocomplete-input',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './google-autocomplete-input.component.html',
  styleUrl: './google-autocomplete-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => GoogleAutocompleteInputComponent),
      multi: true
    }
  ]
})
export class GoogleAutocompleteInputComponent implements OnInit, ControlValueAccessor {
  input = viewChild<ElementRef>('googleAutocompleteInput');

  ngZone = inject(NgZone);

  addressChange = output<any>();

  value: string = '';
  invalid = signal(false);
  touched = signal(false);
  disabled = signal(false);
  private addressComponents = null;
  private onChange: any = () => {};
  private onTouched: any = () => {};

  ngOnInit(): void {
    const autocomplete = new google.maps.places.Autocomplete(this.input().nativeElement, {
      componentRestrictions: { country: ['us'] },
      fields: ['place_id', 'geometry', 'name', 'formatted_address', 'address_components'],
      types: ['address']
    });

    autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = autocomplete.getPlace();

        this.addressComponents = place.address_components;

        const addressDetails = {
          displayAddress: this.input().nativeElement.value,
          streetNumber: this.getComponent('street_number'),
          street: this.getComponent('route'),
          city: this.getComponent('locality'),
          state: this.getComponent('administrative_area_level_1', true),
          postalCode: this.getComponent('postal_code'),
          country: this.getComponent('country'),
          latitude: place.geometry?.location?.lat(),
          longitude: place.geometry?.location?.lng()
        };

        const streetDetails = this.getStreetDetails();

        this.value = streetDetails || '';
        this.invalid.set(false);
        this.onChange(this.value);
        this.addressChange.emit(addressDetails);
        this.onTouched();
      });
    });
  }

  writeValue(value: any): void {
    this.value = value || '';
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = () => {
      this.touched.set(true);
      fn();
    };
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.value = value;

    if (!value) {
      this.invalid.set(true);
      this.onChange(null);
    }
  }

  onInputBlur(event: Event): void {
    this.onTouched();
  }

  private getComponent(type: string, short = false) {
    const nameProperty = short ? 'short_name' : 'long_name';

    return (
      this.addressComponents.find((c) => c.types.includes(type))?.[nameProperty] || ''
    );
  }

  private getStreetDetails() {
    const streetNumber = this.getComponent('street_number');
    const streetName = this.getComponent('route');

    return streetNumber ? `${streetNumber} ${streetName}` : streetName;
  }
}
